import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Apple, TrendingUp, AlertCircle, Loader2, ChefHat, Utensils, Target, Zap, Clock, Lock } from 'lucide-react';
import { toast } from 'sonner';

import { API } from '../config/api';

export default function NutritionAI({ childId, childName }) {
  const [eligibility, setEligibility] = useState(null);
  const [analysis, setAnalysis] = useState(null);
  const [suggestions, setSuggestions] = useState(null);
  const [loading, setLoading] = useState(true);
  const [analyzing, setAnalyzing] = useState(false);
  const [activeTab, setActiveTab] = useState('analysis');

  const fetchAnalysis = useCallback(async () => {
    setAnalyzing(true);
    try {
      const res = await axios.get(`${API}/nutrition-ai/analysis/${childId}`);
      setAnalysis(res.data);
    } catch (err) {
      if (err.response?.status !== 400) {
        toast.error('Failed to load analysis');
      }
    } finally {
      setAnalyzing(false);
    }
  }, [childId]);

  const fetchSuggestions = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/nutrition-ai/meal-suggestions/${childId}`);
      setSuggestions(res.data.suggestions);
    } catch (err) {
      console.error('Failed to load suggestions');
    }
  }, [childId]);

  const checkEligibility = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/nutrition-ai/eligibility/${childId}`);
      setEligibility(res.data);
      if (res.data.eligible) {
        fetchAnalysis();
        fetchSuggestions();
      }
    } catch (err) {
      console.error('Failed to check eligibility');
    } finally {
      setLoading(false);
    }
  }, [childId, fetchAnalysis, fetchSuggestions]);

  useEffect(() => {
    checkEligibility();
  }, [checkEligibility]);

  if (loading) {
    return (
      <div className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-6 animate-pulse">
        <div className="h-6 bg-zinc-800 rounded w-48 mb-4" />
        <div className="h-20 bg-zinc-800 rounded" />
      </div>
    );
  }

  // Not eligible - show progress tracker
  if (!eligibility?.eligible) {
    return (
      <div className="bg-zinc-900/50 border border-zinc-800 rounded-xl overflow-hidden" data-testid="nutrition-ai">
        <div className="p-4 border-b border-zinc-800 flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-amber-500/20 flex items-center justify-center">
            <Lock className="h-5 w-5 text-amber-500" />
          </div>
          <div>
            <h3 className="font-bold text-white">AI Nutrition Coach</h3>
            <p className="text-xs text-zinc-400">Unlock personalized recommendations</p>
          </div>
        </div>
        
        <div className="p-6 text-center">
          <div className="w-20 h-20 rounded-full bg-zinc-800 mx-auto mb-4 flex items-center justify-center">
            <Apple className="h-10 w-10 text-zinc-600" />
          </div>
          
          <h4 className="text-lg font-bold text-white mb-2">
            {eligibility?.days_remaining || 14} more days of tracking needed
          </h4>
          <p className="text-zinc-400 text-sm mb-4">
            Log meals for {eligibility?.min_days_required || 14} days to unlock AI-powered nutrition analysis and personalized meal recommendations.
          </p>
          
          {/* Progress bar */}
          <div className="max-w-xs mx-auto">
            <div className="flex justify-between text-xs text-zinc-500 mb-1">
              <span>{eligibility?.days_tracked || 0} days logged</span>
              <span>{eligibility?.min_days_required || 14} days required</span>
            </div>
            <div className="h-3 bg-zinc-800 rounded-full overflow-hidden">
              <div 
                className="h-full bg-gradient-to-r from-amber-500 to-orange-500 transition-all"
                style={{ width: `${((eligibility?.days_tracked || 0) / (eligibility?.min_days_required || 14)) * 100}%` }}
              />
            </div>
          </div>
          
          <p className="text-xs text-zinc-500 mt-4">
            Go to the Meals tab to start logging your nutrition
          </p>
        </div>
      </div>
    );
  }

  // Eligible - show full analysis
  return (
    <div className="bg-zinc-900/50 border border-zinc-800 rounded-xl overflow-hidden" data-testid="nutrition-ai">
      {/* Header */}
      <div className="p-4 border-b border-zinc-800">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-green-500/20 flex items-center justify-center">
              <Apple className="h-5 w-5 text-green-500" />
            </div>
            <div>
              <h3 className="font-bold text-white">AI Nutrition Coach</h3>
              <p className="text-xs text-zinc-400">
                Based on {analysis?.tracking_summary?.days_analyzed || 14} days of tracking
              </p>
            </div>
          </div>
          <Button
            size="sm"
            onClick={fetchAnalysis}
            disabled={analyzing}
            className="bg-zinc-800 hover:bg-zinc-700"
          >
            {analyzing ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Refresh'}
          </Button>
        </div>
        
        {/* Tabs */}
        <div className="flex gap-2 mt-4">
          {['analysis', 'suggestions'].map(tab => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                activeTab === tab 
                  ? 'bg-[#02b5ff] text-white' 
                  : 'bg-zinc-800 text-zinc-400 hover:text-white'
              }`}
            >
              {tab === 'analysis' ? 'Analysis' : 'Meal Ideas'}
            </button>
          ))}
        </div>
      </div>

      <div className="p-4">
        {activeTab === 'analysis' && analysis?.recommendations && (
          <div className="space-y-4">
            {/* Overall Assessment */}
            <div className="bg-zinc-800/50 rounded-lg p-4">
              <div className="flex items-start gap-3">
                <TrendingUp className="h-5 w-5 text-[#02b5ff] mt-0.5" />
                <div>
                  <h4 className="font-semibold text-white mb-1">Overall Assessment</h4>
                  <p className="text-sm text-zinc-300">{analysis.recommendations.overall_assessment}</p>
                </div>
              </div>
            </div>

            {/* Current Intake vs Targets */}
            {analysis.current_intake && analysis.recommendations.target_macros && (
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                {[
                  { label: 'Calories', current: analysis.current_intake.calories, target: analysis.recommendations.target_macros.calories, unit: '', color: 'blue' },
                  { label: 'Protein', current: analysis.current_intake.protein_g, target: analysis.recommendations.target_macros.protein_g, unit: 'g', color: 'red' },
                  { label: 'Carbs', current: analysis.current_intake.carbs_g, target: analysis.recommendations.target_macros.carbs_g, unit: 'g', color: 'yellow' },
                  { label: 'Fat', current: analysis.current_intake.fat_g, target: analysis.recommendations.target_macros.fat_g, unit: 'g', color: 'green' }
                ].map(({ label, current, target, unit, color }) => {
                  const pct = target > 0 ? Math.min((current / target) * 100, 150) : 0;
                  const status = pct >= 90 && pct <= 110 ? 'good' : pct < 90 ? 'low' : 'high';
                  return (
                    <div key={label} className="bg-zinc-800/50 rounded-lg p-3">
                      <div className="text-xs text-zinc-500 mb-1">{label}</div>
                      <div className="text-lg font-bold text-white">{current}{unit}</div>
                      <div className="text-xs text-zinc-400">Target: {target}{unit}</div>
                      <div className="mt-2 h-1.5 bg-zinc-700 rounded-full overflow-hidden">
                        <div 
                          className={`h-full transition-all ${
                            status === 'good' ? 'bg-green-500' : status === 'low' ? 'bg-yellow-500' : 'bg-orange-500'
                          }`}
                          style={{ width: `${Math.min(pct, 100)}%` }}
                        />
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            {/* Recommendations */}
            <div className="grid gap-3">
              {[
                { icon: Zap, title: 'Protein', text: analysis.recommendations.protein_recommendation, color: 'red' },
                { icon: Target, title: 'Carbohydrates', text: analysis.recommendations.carbs_recommendation, color: 'yellow' },
                { icon: Apple, title: 'Fats', text: analysis.recommendations.fat_recommendation, color: 'green' },
                { icon: Clock, title: 'Meal Timing', text: analysis.recommendations.meal_timing, color: 'purple' }
              ].map(({ icon: Icon, title, text, color }) => (
                <div key={title} className="bg-zinc-800/30 rounded-lg p-3 flex items-start gap-3">
                  <Icon className={`h-4 w-4 mt-0.5 text-${color}-500`} />
                  <div>
                    <h5 className="text-sm font-semibold text-white">{title}</h5>
                    <p className="text-xs text-zinc-400 mt-0.5">{text}</p>
                  </div>
                </div>
              ))}
            </div>

            {/* Food Suggestions */}
            {analysis.recommendations.food_suggestions && (
              <div className="bg-green-500/10 border border-green-500/30 rounded-lg p-3">
                <h4 className="text-sm font-semibold text-green-400 mb-2 flex items-center gap-2">
                  <ChefHat className="h-4 w-4" />
                  Foods to Add
                </h4>
                <ul className="space-y-1">
                  {analysis.recommendations.food_suggestions.map((food, i) => (
                    <li key={i} className="text-xs text-zinc-300 flex items-center gap-2">
                      <span className="text-green-500">•</span> {food}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Caution */}
            {analysis.recommendations.caution && (
              <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-3">
                <div className="flex items-start gap-2">
                  <AlertCircle className="h-4 w-4 text-amber-500 mt-0.5" />
                  <p className="text-xs text-amber-200">{analysis.recommendations.caution}</p>
                </div>
              </div>
            )}
          </div>
        )}

        {activeTab === 'suggestions' && suggestions && (
          <div className="space-y-4">
            {Object.entries(suggestions).map(([mealType, meals]) => (
              <div key={mealType} className="bg-zinc-800/30 rounded-lg p-4">
                <h4 className="text-sm font-bold text-white uppercase tracking-wider mb-3 flex items-center gap-2">
                  <Utensils className="h-4 w-4 text-[#02b5ff]" />
                  {mealType}
                </h4>
                <div className="grid gap-2">
                  {meals.map((meal, i) => (
                    <div key={i} className="bg-zinc-800/50 rounded-lg p-3 flex justify-between items-start">
                      <div>
                        <p className="font-medium text-white text-sm">{meal.name}</p>
                        <p className="text-xs text-zinc-400">{meal.description}</p>
                      </div>
                      <div className="text-right text-xs">
                        <div className="text-white">{meal.calories} cal</div>
                        <div className="text-zinc-500">{meal.protein}g protein</div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
